import React, { useEffect, useState } from 'react';
import { geocodeCached } from './googleMapsTravel.js';
import {
  fetchForecast,
  findForecastForDate,
  daysUntil,
  fetchHourlyForecast,
  findHoursForDate,
} from './googleWeather.js';

// Pick the city out of an address string. Skips a street-style first segment
// (one that starts with a digit) so "1234 Main St, Vernon, BC" → "Vernon",
// "Vernon, BC" → "Vernon", and "Penticton" → "Penticton".
const parseCity = (address) => {
  if (!address) return null;
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  const startsWithNumber = /^\d/.test(parts[0]);
  return startsWithNumber && parts.length >= 2 ? parts[1] : parts[0];
};

const MUTED_MESSAGES = {
  loading: 'Loading weather...',
  out_of_range: 'Weather available closer to date',
  unavailable: 'Weather unavailable',
};

const CARDINAL_SHORT = {
  NORTH: 'N', NORTHEAST: 'NE', EAST: 'E', SOUTHEAST: 'SE',
  SOUTH: 'S', SOUTHWEST: 'SW', WEST: 'W', NORTHWEST: 'NW',
};

const thresholdColor = (value, thresholds, fallback) => {
  if (value == null) return fallback;
  for (const [min, color] of thresholds) {
    if (value >= min) return color;
  }
  return fallback;
};

const uvLabel = (uv) => {
  if (uv == null) return null;
  if (uv >= 11) return `${uv} (Extreme)`;
  if (uv >= 8) return `${uv} (Very high)`;
  if (uv >= 6) return `${uv} (High)`;
  if (uv >= 3) return `${uv} (Moderate)`;
  return `${uv} (Low)`;
};

const formatTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return null;
  }
};

const formatPrecip = (precip) => {
  if (!precip) return null;
  const pct = precip.probability?.percent;
  const qty = precip.qpf?.quantity;
  const snow = precip.snowQpf?.quantity;
  if (pct == null) return null;
  const parts = [`${pct}%`];
  if (qty > 0) parts.push(`${qty} mm`);
  if (snow > 0) parts.push(`${snow} mm snow`);
  return parts.join(' — ');
};

const formatWind = (wind) => {
  if (wind?.speed?.value == null) return null;
  const dir = wind.direction?.cardinal;
  const dirShort = dir ? (CARDINAL_SHORT[dir] || dir) : '';
  return `${dirShort} ${Math.round(wind.speed.value)} km/h`.trim();
};

const formatMoonPhase = (phase) => {
  if (!phase) return null;
  return phase.toLowerCase().split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
};

const PeriodColumn = ({ label, period, feelsLike }) => {
  if (!period) return null;
  const cond = period.weatherCondition || {};
  const iconUri = cond.iconBaseUri ? `${cond.iconBaseUri}.svg` : null;
  const rows = [
    feelsLike != null && ['Feels like', `${Math.round(feelsLike)}°`],
    period.relativeHumidity != null && ['Humidity', `${period.relativeHumidity}%`],
    period.uvIndex != null && period.uvIndex > 0 && ['UV index', uvLabel(period.uvIndex)],
    period.precipitation && ['Precipitation', formatPrecip(period.precipitation)],
    period.wind?.speed?.value != null && ['Wind', formatWind(period.wind)],
    period.wind?.gust?.value != null && ['Gust', `${Math.round(period.wind.gust.value)} km/h`],
    period.cloudCover != null && ['Cloud cover', `${period.cloudCover}%`],
    period.thunderstormProbability > 0 && ['Thunderstorms', `${period.thunderstormProbability}%`],
  ].filter((row) => Array.isArray(row) && row[1] != null && row[1] !== '');

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {iconUri && <img src={iconUri} alt="" className="w-6 h-6" />}
        <div>
          <div className="text-white font-semibold">{label}</div>
          {cond.description?.text && <div className="text-gray-400 text-[11px]">{cond.description.text}</div>}
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-gray-400">{k}</dt>
            <dd className="text-gray-300">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const formatHourLabel = (h) => {
  if (h == null) return '';
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
};

const HourCell = ({ hour, selected, onSelect }) => {
  const dt = hour.displayDateTime || {};
  const hr = dt.hours;
  const cond = hour.weatherCondition || {};
  const iconUri = cond.iconBaseUri ? `${cond.iconBaseUri}.svg` : null;
  const temp = hour.temperature?.degrees;
  const precipPct = hour.precipitation?.probability?.percent;
  const windKmh = hour.wind?.speed?.value;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded shrink-0 min-w-[48px] transition-colors ${
        selected
          ? 'bg-green-600/25 ring-1 ring-green-600/60'
          : 'bg-gray-700/50 hover:bg-gray-700/80'
      }`}
      aria-pressed={selected}
      title="Click for details"
    >
      <div className="text-gray-400 text-[10px]">{formatHourLabel(hr)}</div>
      {iconUri && <img src={iconUri} alt={cond.description?.text || ''} className="w-5 h-5" loading="lazy" />}
      <div className="text-white text-xs font-medium">
        {temp != null ? `${Math.round(temp)}°` : '—'}
      </div>
      {precipPct != null && precipPct > 10 && (
        <div className="text-amber-300 text-[10px]" title="Precipitation chance">{precipPct}%</div>
      )}
      {windKmh != null && windKmh > 20 && (
        <div className="text-orange-300 text-[10px]" title="Wind">{Math.round(windKmh)}</div>
      )}
    </button>
  );
};

const HourDetail = ({ hour }) => {
  const dt = hour.displayDateTime || {};
  const cond = hour.weatherCondition || {};
  const iconUri = cond.iconBaseUri ? `${cond.iconBaseUri}.svg` : null;
  const temp = hour.temperature?.degrees;
  const feels = hour.feelsLikeTemperature?.degrees;

  const rows = [
    feels != null && ['Feels like', `${Math.round(feels)}°`],
    hour.relativeHumidity != null && ['Humidity', `${hour.relativeHumidity}%`],
    hour.uvIndex != null && hour.uvIndex > 0 && ['UV index', uvLabel(hour.uvIndex)],
    hour.precipitation && ['Precipitation', formatPrecip(hour.precipitation)],
    hour.wind?.speed?.value != null && ['Wind', formatWind(hour.wind)],
    hour.wind?.gust?.value != null && ['Gust', `${Math.round(hour.wind.gust.value)} km/h`],
    hour.cloudCover != null && ['Cloud cover', `${hour.cloudCover}%`],
    hour.thunderstormProbability > 0 && ['Thunderstorms', `${hour.thunderstormProbability}%`],
    hour.visibility?.distance != null && ['Visibility', `${hour.visibility.distance} ${(hour.visibility.unit || 'km').toLowerCase()}`],
    hour.dewPoint?.degrees != null && ['Dew point', `${Math.round(hour.dewPoint.degrees)}°`],
  ].filter((row) => Array.isArray(row) && row[1] != null && row[1] !== '');

  return (
    <div className="mt-2 px-3 py-2 bg-gray-700/40 rounded">
      <div className="flex items-center gap-2 mb-1.5">
        {iconUri && <img src={iconUri} alt="" className="w-6 h-6" />}
        <div>
          <div className="text-white font-semibold">
            {formatHourLabel(dt.hours)}
            {temp != null && <span className="ml-2 text-gray-300">{Math.round(temp)}°</span>}
          </div>
          {cond.description?.text && (
            <div className="text-gray-400 text-[11px]">{cond.description.text}</div>
          )}
        </div>
      </div>
      {rows.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto_1fr] gap-x-3 gap-y-0.5">
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-gray-400">{k}</dt>
              <dd className="text-gray-300">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
};

const ExpandedPanel = ({ day, hourly, hourlyStatus }) => {
  const sun = day.sunEvents || {};
  const moon = day.moonEvents || {};
  const sunrise = formatTime(sun.sunriseTime);
  const sunset = formatTime(sun.sunsetTime);
  const moonPhase = formatMoonPhase(moon.moonPhase);

  const [selectedHourIdx, setSelectedHourIdx] = useState(null);

  // Reset selection if hourly data changes (different day, new fetch)
  useEffect(() => { setSelectedHourIdx(null); }, [hourly]);

  return (
    <div className="px-3 pb-3 pt-2 border-t border-gray-500/30 text-xs">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PeriodColumn label="Daytime" period={day.daytimeForecast} feelsLike={day.feelsLikeMaxTemperature?.degrees} />
        <PeriodColumn label="Nighttime" period={day.nighttimeForecast} feelsLike={day.feelsLikeMinTemperature?.degrees} />
      </div>

      <div className="mt-3 pt-2 border-t border-gray-500/20">
        <div className="text-gray-400 mb-1.5">Hourly (6am — 8pm) — click an hour for details</div>
        {hourlyStatus === 'loading' && (
          <div className="flex items-center gap-2 text-gray-400">
            <div className="animate-spin h-3 w-3 border-2 border-gray-500 border-t-gray-300 rounded-full" />
            Loading hourly...
          </div>
        )}
        {hourlyStatus === 'unavailable' && (
          <div className="text-gray-400">Hourly forecast unavailable</div>
        )}
        {hourlyStatus === 'ok' && hourly.length === 0 && (
          <div className="text-gray-400">No hourly data for this day</div>
        )}
        {hourlyStatus === 'ok' && hourly.length > 0 && (
          <>
            <div className="flex overflow-x-auto gap-1.5 pb-1">
              {hourly.map((h, i) => (
                <HourCell
                  key={i}
                  hour={h}
                  selected={selectedHourIdx === i}
                  onSelect={() => setSelectedHourIdx((cur) => (cur === i ? null : i))}
                />
              ))}
            </div>
            {selectedHourIdx != null && hourly[selectedHourIdx] && (
              <HourDetail hour={hourly[selectedHourIdx]} />
            )}
          </>
        )}
      </div>

      {(sunrise || sunset || moonPhase) && (
        <div className="mt-3 pt-2 border-t border-gray-500/20 flex flex-wrap gap-x-4 gap-y-1 text-gray-400">
          {sunrise && <span><span className="text-gray-400">Sunrise</span> {sunrise}</span>}
          {sunset && <span><span className="text-gray-400">Sunset</span> {sunset}</span>}
          {moonPhase && <span><span className="text-gray-400">Moon</span> {moonPhase}</span>}
        </div>
      )}
    </div>
  );
};

const WeatherStrip = ({ dateISO, address }) => {
  const [state, setState] = useState({ status: 'idle' });
  const [expanded, setExpanded] = useState(false);
  const [hourly, setHourly] = useState({ status: 'idle', hours: [] });

  const city = parseCity(address);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const diff = daysUntil(dateISO);
      if (diff == null || diff < 0) return setState({ status: 'idle' });
      if (diff > 9) return setState({ status: 'out_of_range' });
      if (!address || !address.trim()) return setState({ status: 'unavailable' });

      setState({ status: 'loading' });

      const geo = await geocodeCached(address);
      if (cancelled) return;
      if (!geo) return setState({ status: 'unavailable' });

      const forecast = await fetchForecast(geo.lat, geo.lng);
      if (cancelled) return;

      const day = forecast && findForecastForDate(forecast, dateISO);
      if (cancelled) return;
      setState(day ? { status: 'ok', day, geo } : { status: 'unavailable' });
    };

    run();
    return () => { cancelled = true; };
  }, [dateISO, address]);

  // Lazy hourly fetch — only when the user expands the strip.
  // hourly.status is intentionally NOT in deps: putting it there would cause
  // the setHourly('loading') call below to re-trigger this effect, which would
  // invoke its own cleanup and cancel the in-flight async before it resolves.
  // Underlying fetchHourlyForecast is cached, so re-firing on toggle is cheap.
  useEffect(() => {
    if (!expanded || state.status !== 'ok' || !state.geo) return;
    let cancelled = false;
    setHourly({ status: 'loading', hours: [] });
    (async () => {
      const all = await fetchHourlyForecast(state.geo.lat, state.geo.lng);
      if (cancelled) return;
      if (!all) return setHourly({ status: 'unavailable', hours: [] });
      const hours = findHoursForDate(all, dateISO);
      setHourly({ status: 'ok', hours });
    })();
    return () => { cancelled = true; };
  }, [expanded, state.status, state.geo, dateISO]);

  if (state.status === 'idle') return null;

  if (state.status !== 'ok') {
    return (
      <div className="mb-3 px-3 py-1.5 rounded text-xs flex items-center gap-3 bg-gray-700/40 text-gray-400">
        {state.status === 'loading' && (
          <div className="animate-spin h-3 w-3 border-2 border-gray-500 border-t-gray-300 rounded-full"></div>
        )}
        {MUTED_MESSAGES[state.status]}
      </div>
    );
  }

  const day = state.day;
  const dt = day.daytimeForecast || {};
  const cond = dt.weatherCondition || {};
  const iconUri = cond.iconBaseUri ? `${cond.iconBaseUri}.svg` : null;
  const condText = cond.description?.text || '';
  const maxT = day.maxTemperature?.degrees;
  const minT = day.minTemperature?.degrees;
  const precipPct = dt.precipitation?.probability?.percent;
  const windKmh = dt.wind?.speed?.value;

  const precipColor = thresholdColor(precipPct, [[50, 'text-amber-300'], [30, 'text-orange-300']], 'text-gray-400');
  const windColor = thresholdColor(windKmh, [[30, 'text-amber-300'], [20, 'text-orange-300']], 'text-gray-400');

  return (
    <div className="mb-3 rounded bg-gray-700/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-1.5 flex items-center gap-3 text-xs text-gray-300 text-left hover:bg-gray-700/80 transition-colors"
        aria-expanded={expanded}
        title={expanded ? 'Hide detailed forecast' : 'Show detailed forecast'}
      >
        {iconUri && (
          <img src={iconUri} alt={condText || 'weather'} className="w-7 h-7 shrink-0" loading="lazy" />
        )}
        {city && (
          <span className="font-semibold text-white whitespace-nowrap">{city}</span>
        )}
        {(maxT != null || minT != null) && (
          <span className="font-semibold text-white whitespace-nowrap">
            {maxT != null ? `${Math.round(maxT)}°` : '—'}
            {minT != null && <span className="text-gray-400 font-normal"> / {Math.round(minT)}°</span>}
          </span>
        )}
        {condText && <span className="text-gray-300 truncate">{condText}</span>}
        <div className="ml-auto flex items-center gap-3 shrink-0">
          {precipPct != null && precipPct > 10 && (
            <span className={precipColor}>Rain {precipPct}%</span>
          )}
          {windKmh != null && windKmh > 20 && (
            <span className={windColor}>Wind {Math.round(windKmh)} km/h</span>
          )}
          <span className="text-gray-400 text-[10px]" aria-hidden>{expanded ? '▲' : '▼'}</span>
        </div>
      </button>
      {expanded && (
        <ExpandedPanel day={day} hourly={hourly.hours} hourlyStatus={hourly.status} />
      )}
    </div>
  );
};

export default WeatherStrip;
